import React, { useState } from "react";
import { HttpError, useTranslate } from "@refinedev/core";
import { Alert, Form, Input } from "antd";
import { Button } from "@libertify/ui";
import { PASSWORD_COMPLEXITY_PATTERN } from "constants/auth";
import { confirmPasswordRules } from "helpers/inputValidation/confirmPasswordValidation";
import { useChangePassword } from "../hooks/useChangePassword";
import {
  isAttemptLimitExceededError,
  isIncorrectCurrentPasswordError,
} from "../helpers";

type ChangePasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type ChangePasswordProps = Readonly<{
  onSuccess?: () => void;
}>;

const ChangePassword: React.FC<ChangePasswordProps> = ({ onSuccess }) => {
  const t = useTranslate();
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const { changePassword, isLoading } = useChangePassword();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleFinish = async (values: ChangePasswordFormValues) => {
    setSubmitError(null);
    try {
      await changePassword({
        previous_password: values.currentPassword,
        proposed_password: values.newPassword,
      });
      form.resetFields();
      onSuccess?.();
    } catch (error) {
      const httpError = error as HttpError;
      if (isIncorrectCurrentPasswordError(httpError)) {
        setSubmitError(t("pages.user.changePassword.incorrectCurrentPassword"));
      } else if (isAttemptLimitExceededError(httpError)) {
        setSubmitError(t("pages.user.changePassword.attemptLimitExceeded"));
      }
      // other errors: errorNotification in useChangePassword already surfaces this
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={() => setSubmitError(null)}
      onFinish={(values) => {
        handleFinish(values).catch((error) => {
          console.error("Failed to change password", error);
        });
      }}
    >
      <Form.Item
        name="currentPassword"
        label={t("pages.user.changePassword.currentPassword")}
        rules={[
          {
            required: true,
            message: t("pages.user.changePassword.pleaseEnterCurrentPassword"),
          },
        ]}
      >
        <Input.Password
          size="large"
          autoComplete="current-password"
          placeholder={t(
            "pages.user.changePassword.currentPasswordPlaceholder"
          )}
        />
      </Form.Item>

      <Form.Item
        name="newPassword"
        label={t("pages.user.changePassword.newPassword")}
        validateFirst
        rules={[
          {
            required: true,
            message: t("pages.user.changePassword.pleaseEnterNewPassword"),
          },
          {
            min: 12,
            message: t("pages.auth.new-register.passwordMustBe"),
          },
          {
            pattern: PASSWORD_COMPLEXITY_PATTERN,
            message: t("pages.auth.new-register.passwordMustContain"),
          },
        ]}
      >
        <Input.Password
          size="large"
          autoComplete="new-password"
          placeholder={t("pages.user.changePassword.newPasswordPlaceholder")}
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label={t("pages.user.changePassword.confirmPassword")}
        dependencies={["newPassword"]}
        style={{ marginBottom: 32 }}
        rules={confirmPasswordRules(t, "newPassword")}
      >
        <Input.Password
          size="large"
          autoComplete="new-password"
          placeholder={t(
            "pages.user.changePassword.confirmPasswordPlaceholder"
          )}
        />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button
          htmlType="submit"
          variant="secondary"
          size="l"
          loading={isLoading}
          block
        >
          {t("pages.user.changePassword.submit")}
        </Button>
      </Form.Item>

      {submitError ? (
        <Alert
          type="error"
          showIcon
          message={submitError}
          style={{ marginTop: 16 }}
        />
      ) : null}
    </Form>
  );
};

export default ChangePassword;
